import { useEffect, useState } from 'react'

export interface ActiveEditor {
  userName: string
  active: boolean
  lastActive: string
}

export interface EditorPresence {
  editors: ActiveEditor[]
  status: 'loading' | 'ready' | 'error'
}

export function watchEditorPresence(
  baseUrl: string,
  path: string,
  onChange: (presence: EditorPresence) => void,
  request: typeof fetch = fetch
) {
  const controller = new AbortController()
  let editors: ActiveEditor[] = []
  let loaded = false
  let registerFailed = false
  let fetchFailed = false
  let registering = false
  let fetching = false
  const publish = () => {
    if (!controller.signal.aborted) {
      let status: EditorPresence['status'] = loaded ? `ready` : `loading`
      if (registerFailed || fetchFailed) status = `error`
      onChange({ editors, status })
    }
  }
  const call = (endpoint: string) =>
    request(
      `${baseUrl}/puxapi/collaboration/${endpoint}?path=${encodeURIComponent(
        path
      )}`,
      { credentials: `include`, signal: controller.signal }
    )
  const register = async () => {
    if (registering) return
    registering = true
    try {
      const response = await call(`register-editor`)
      if (!response.ok) throw new Error(`Registration failed`)
      registerFailed = false
    } catch {
      registerFailed = true
    } finally {
      registering = false
      publish()
    }
  }
  const update = async () => {
    if (fetching) return
    fetching = true
    try {
      const response = await call(`active-editors`)
      if (!response.ok) throw new Error(`Presence unavailable`)
      const result: ActiveEditor[] = await response.json()
      if (!Array.isArray(result)) throw new Error(`Invalid presence`)
      loaded = true
      editors = result
      fetchFailed = false
    } catch {
      fetchFailed = true
    } finally {
      fetching = false
      publish()
    }
  }
  void register().then(() => {
    if (!controller.signal.aborted) void update()
  })
  const registration = setInterval(register, 1500)
  const polling = setInterval(update, 15000)
  return () => {
    controller.abort()
    clearInterval(registration)
    clearInterval(polling)
  }
}

export function useEditorPresence(pageContentItemId: string, enabled: boolean) {
  const [presence, setPresence] = useState<EditorPresence>({
    editors: [],
    status: `loading`,
  })
  useEffect(() => {
    setPresence({ editors: [], status: `loading` })
    if (!enabled || !pageContentItemId) return
    return watchEditorPresence(
      process.env.GATSBY_ORCHARD_API_URL ?? ``,
      `/Admin/Contents/ContentItems/${pageContentItemId}/Edit`,
      setPresence
    )
  }, [pageContentItemId, enabled])
  return presence
}
